import * as React from 'react';
import { Badge } from '@/hen-ui/atomics/Badge';
import { Button } from '@/hen-ui/atomics/Button';
import { Heading } from '@/hen-ui/atomics/Heading';
import { Image } from '@/hen-ui/atomics/Image';
import { Link } from '@/hen-ui/atomics/Link';
import { List, ListItem } from '@/hen-ui/atomics/List';
import { Separator } from '@/hen-ui/atomics/Separator';
import { Text } from '@/hen-ui/atomics/Text';
import { Icon } from '@/hen-ui/atomics/Icon';
import { CardContent } from '@/hen-ui/composites/Card';
import { CardRoot } from '@/hen-ui/composites/Card/Card';
import { Flex } from '@/hen-ui/composites/Flex';
import { Rating } from '@/hen-ui/composites/Rating';
import { COMPONENT_IDS } from '@/hen-ui/catalog/component-ids';
import { cn } from '@/hen-ui/utils';
import {
  studentLoansBannerBadgeClasses,
  studentLoansBannerBulletsClasses,
  studentLoansBannerBulletsColumnClasses,
  studentLoansBannerBulletsContainerClasses,
  studentLoansBannerCardContentClasses,
  studentLoansBannerCtaButtonClasses,
  studentLoansBannerCtaColumnClasses,
  studentLoansBannerDataPointRowClasses,
  studentLoansBannerDataPointsClasses,
  studentLoansBannerDisclosureRowClasses,
  studentLoansBannerDisclosureTextClasses,
  studentLoansBannerLearnMoreClasses,
  studentLoansBannerMainRowClasses,
  studentLoansBannerPartnerClasses,
  studentLoansBannerRootClasses,
  studentLoansBannerSeparatorClasses,
} from './student-loans-banner.layout';
import type {
  StudentLoansBannerDataPoint,
  StudentLoansBannerLayout,
  StudentLoansBannerProps,
} from './types';

function asStringArray(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter(
      (item): item is string =>
        typeof item === 'string' && item.trim().length > 0,
    );
  }
  if (typeof value === 'string' && value.trim().length > 0) {
    return [value];
  }
  return [];
}

function asDataPoints(value: unknown): StudentLoansBannerDataPoint[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.flatMap((item) => {
    if (typeof item !== 'object' || item === null) {
      return [];
    }
    const label = (item as { label?: unknown }).label;
    const pointValue = (item as { value?: unknown }).value;
    if (typeof label !== 'string' || typeof pointValue !== 'string') {
      return [];
    }
    return [{ label, value: pointValue }];
  });
}

function splitBulletsIntoColumns<T>(items: T[], columnCount: number): T[][] {
  const columnSize = Math.ceil(items.length / columnCount);
  return Array.from({ length: columnCount }, (_, columnIndex) =>
    items.slice(columnIndex * columnSize, (columnIndex + 1) * columnSize),
  ).filter((column) => column.length > 0);
}

function BannerBulletItem({
  children,
  layout,
}: React.PropsWithChildren<{ layout: StudentLoansBannerLayout }>) {
  return (
    <ListItem className='min-w-0'>
      <Text variant={layout === 'mobile' ? 'b5' : 'b4'} tag='span'>
        {children}
      </Text>
    </ListItem>
  );
}

function BannerBulletList({
  bullets,
  layout,
  className,
  'data-student-loans-banner-bullets-column': bulletsColumnMarker,
}: {
  bullets: string[];
  layout: StudentLoansBannerLayout;
  className?: string;
  'data-student-loans-banner-bullets-column'?: string;
}) {
  return (
    <List
      bulletStyle='check'
      className={cn(studentLoansBannerBulletsClasses(layout), className)}
      data-student-loans-banner-bullets-column={bulletsColumnMarker}
    >
      {bullets.map((bullet, index) => (
        <BannerBulletItem key={`${bullet}-${index}`} layout={layout}>
          {bullet}
        </BannerBulletItem>
      ))}
    </List>
  );
}

function BannerDataPointRow({
  label,
  value,
  layout,
}: {
  label: string;
  value: string;
  layout: StudentLoansBannerLayout;
}): React.ReactElement {
  return (
    <Flex
      direction='column'
      gap='min'
      className={studentLoansBannerDataPointRowClasses(layout)}
    >
      <Text variant={layout === 'mobile' ? 'l2' : 'l1'} color='default'>
        {label}
      </Text>
      <Text variant='b3' weight='bold' color='heading' tag='span'>
        {value}
      </Text>
    </Flex>
  );
}

export const StudentLoansBanner = React.forwardRef<
  HTMLDivElement,
  StudentLoansBannerProps
>(
  (
    {
      layout = 'inContent',
      title,
      subtitle,
      badgeText,
      partner,
      bullets,
      cta,
      learnMore,
      dataPoints,
      disclosure,
      onCtaClick,
      ...props
    },
    ref,
  ) => {
    const normalizedBullets = asStringArray(bullets);
    const normalizedDataPoints = asDataPoints(dataPoints);

    return (
      <CardRoot
        ref={ref}
        variant='background-gray'
        border='strokeLight'
        rounded='lg'
        className='w-full'
        role='region'
        aria-label={title}
        data-hen-id={COMPONENT_IDS.StudentLoansBanner}
        data-student-loans-banner-layout={layout}
        {...props}
      >
        <CardContent
          contentPadding='none'
          className={studentLoansBannerCardContentClasses(layout)}
        >
          <div className={studentLoansBannerRootClasses(layout)}>
            <Flex direction='column' gap='min'>
              <Heading variant={layout === 'mobile' ? 'h4' : 'h5'}>
                {title}
              </Heading>
              <Text variant='b4' color='default'>
                {subtitle}
              </Text>
            </Flex>

            <Badge
              variant='outline'
              size='sm'
              className={studentLoansBannerBadgeClasses(layout)}
            >
              <Icon icon='flame' size='sm' color='primary' aria-hidden />
              <Text
                variant={layout === 'mobile' ? 'b5' : 'b4'}
                weight='bold'
                color='heading'
                tag='span'
              >
                {badgeText}
              </Text>
            </Badge>

            <div className={studentLoansBannerMainRowClasses(layout)}>
              <Flex
                direction='column'
                className={studentLoansBannerPartnerClasses(layout)}
              >
                <Text variant='l2' weight='bold' color='subtext' tag='span'>
                  {partner.label}
                </Text>
                <Image
                  src={partner.logo}
                  alt={partner.name}
                  className='aspect-logo-box max-h-logo-box max-w-logo-box w-auto object-contain object-left'
                />
                {partner.showRating !== false && (
                  <Rating
                    scoreSize={layout === 'mobile' ? 'compact' : 'default'}
                    rating={partner.rating}
                    ratingCount={partner.ratingCount}
                    ratingCountAriaSuffix=' reviews'
                    showRatingTooltip={partner.showRatingTooltip}
                    ratingTooltip={partner.ratingTooltip}
                  />
                )}
              </Flex>

              {layout === 'fullWidth' ? (
                <div
                  className={studentLoansBannerBulletsContainerClasses(layout)}
                  data-student-loans-banner-bullets=''
                >
                  {splitBulletsIntoColumns(normalizedBullets, 2).map(
                    (column, columnIndex) => (
                      <BannerBulletList
                        key={columnIndex}
                        bullets={column}
                        layout={layout}
                        className={studentLoansBannerBulletsColumnClasses()}
                        data-student-loans-banner-bullets-column=''
                      />
                    ),
                  )}
                </div>
              ) : (
                <BannerBulletList bullets={normalizedBullets} layout={layout} />
              )}

              <Flex
                className={studentLoansBannerCtaColumnClasses(layout)}
                direction='column'
                align='center'
              >
                <Button
                  asChild
                  variant='primaryFill'
                  size='sm'
                  className={studentLoansBannerCtaButtonClasses(layout)}
                >
                  <a
                    href={cta.href}
                    target='_blank'
                    rel='noopener noreferrer sponsored'
                    onClick={onCtaClick}
                  >
                    {cta.label}
                    <Icon icon='arrow-right' size='sm' aria-hidden />
                  </a>
                </Button>
                {cta.subtext && (
                  <Text variant='l2' color='subtext' className='text-center'>
                    {cta.subtext}
                  </Text>
                )}
              </Flex>
            </div>

            <Separator className={studentLoansBannerSeparatorClasses} />

            <div className={studentLoansBannerDataPointsClasses(layout)}>
              {normalizedDataPoints.map((point, index) => (
                <BannerDataPointRow
                  key={`${point.label}-${index}`}
                  label={point.label}
                  value={point.value}
                  layout={layout}
                />
              ))}
            </div>

            <div
              data-student-loans-banner-disclosure=''
              className={studentLoansBannerDisclosureRowClasses(layout)}
            >
              <Text
                variant='l2'
                color='default'
                className={studentLoansBannerDisclosureTextClasses(layout)}
              >
                {disclosure}
              </Text>
              {learnMore && (
                <Link
                  href={learnMore.href}
                  variant='primary'
                  size='sm'
                  weight='bold'
                  target='_blank'
                  rel='noopener noreferrer sponsored'
                  className={studentLoansBannerLearnMoreClasses(layout)}
                >
                  {learnMore.label}
                  <Icon icon='arrow-right' size='sm' aria-hidden />
                </Link>
              )}
            </div>
          </div>
        </CardContent>
      </CardRoot>
    );
  },
);

StudentLoansBanner.displayName = 'StudentLoansBanner';
